// Thin fetch wrapper for the /api/v1 endpoints.
// All endpoints are JSON-only (ActionController::API), no CSRF needed.

const BASE = "/api/v1";

// 비정상 응답을 Error로 변환한다. message 포맷은 기존과 동일하게 유지해 e.message를
// 파싱하던 호출부와의 호환을 지키되, 서버가 준 구조화 정보(error 코드·message·details·
// status)를 별도 필드로 붙인다. apiErrorMessage가 이 필드들로 구체적 한글 사유를
// 복원한다(이게 없으면 "422 ... {json}" 문자열이 통째로 상태 폴백으로 빠져 일반
// 문구만 노출됐다).
function httpError(status: number, statusText: string, detail: any): Error {
  const err: any = new Error(`${status} ${statusText} ${JSON.stringify(detail)}`);
  err.status = status;
  err.code = detail?.error ?? null;
  err.serverMessage = detail?.message ?? null;
  err.details = detail?.details ?? null;
  // 코드/메시지 외의 부가 필드(예: 409 duplicate_elderly 의 elderly_id)를 호출부가
  // 읽을 수 있도록 원본 body 도 그대로 붙인다.
  err.body = detail ?? null;
  return err;
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(BASE + path, {
    method,
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) {
    let detail: unknown = null;
    try {
      detail = await res.json();
    } catch {
      /* not JSON */
    }
    throw httpError(res.status, res.statusText, detail);
  }
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

// 멀티파트(파일 첨부) 요청. JSON 헬퍼와 동일한 에러 계약을 유지하되 Content-Type은
// 브라우저가 boundary와 함께 자동 설정하도록 비운다.
async function requestForm<T>(method: string, path: string, form: FormData): Promise<T> {
  const res = await fetch(BASE + path, {
    method,
    headers: { Accept: "application/json" },
    body: form,
    credentials: "same-origin",
  });
  if (!res.ok) {
    let detail: unknown = null;
    try { detail = await res.json(); } catch { /* not JSON */ }
    throw httpError(res.status, res.statusText, detail);
  }
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

// attrs를 FormData로 직렬화(care_manager[key]=value, 파일 포함). null/undefined는 생략.
function toForm(resource: string, attrs: Record<string, unknown>): FormData {
  const sing = singular(resource);
  const form = new FormData();
  for (const [k, v] of Object.entries(attrs)) {
    if (v === null || v === undefined) continue;
    form.append(`${sing}[${k}]`, v instanceof File ? v : String(v));
  }
  return form;
}

export const api = {
  list:   <T>(resource: string, query: Record<string, string> = {}) => {
    const qs = new URLSearchParams(query).toString();
    return request<T[]>("GET", `/${resource}${qs ? "?" + qs : ""}`);
  },
  get:    <T>(resource: string, id: number | string)               => request<T>("GET",    `/${resource}/${id}`),
  create: <T>(resource: string, attrs: object)                     => request<T>("POST",   `/${resource}`, { [singular(resource)]: attrs }),
  update: <T>(resource: string, id: number | string, attrs: object) => request<T>("PATCH",  `/${resource}/${id}`, { [singular(resource)]: attrs }),
  remove: (resource: string, id: number | string)                   => request<void>("DELETE", `/${resource}/${id}`),
  // 파일 첨부가 포함된 생성/수정 (멀티파트).
  createForm: <T>(resource: string, attrs: Record<string, unknown>)                     => requestForm<T>("POST",  `/${resource}`, toForm(resource, attrs)),
  updateForm: <T>(resource: string, id: number | string, attrs: Record<string, unknown>) => requestForm<T>("PATCH", `/${resource}/${id}`, toForm(resource, attrs)),
};

// 서비스 의뢰 — 케이스와 기관 급여를 잇는 유일한 레코드.
// 상태 변경은 referral 바디 밖 top-level 파라미터라 별도 헬퍼로 둔다.
export const referrals = {
  list:   (query: Record<string, string> = {}) => {
    const qs = new URLSearchParams(query).toString();
    return request<any>("GET", `/referrals${qs ? "?" + qs : ""}`);
  },
  get:    (id: number | string) => request<any>("GET", `/referrals/${id}`),
  send:   (attrs: { case_record_id: number; organization_service_id: number; note?: string; match_id?: number }) =>
    request<any>("POST", "/referrals", { referral: attrs }),
  respond: (id: number | string, status: string, response_note?: string) =>
    request<any>("PATCH", `/referrals/${id}`, { status, response_note }),
};

// 자원은행 조건검색. **대상자 식별자를 보내지 않는다** — 조건만 실어 보낸다
// (자원/대상자 논리 분리 계약). 배열 파라미터는 Rails 관례대로 key[] 로 편다.
export const resourceSearch = {
  find: (cond: {
    service_code: string; dong?: string; needed_from?: string;
    required_tags?: string[]; preferred_tags?: string[]; limit?: number;
    lat?: number; lng?: number;
  }) => {
    const qs = new URLSearchParams();
    qs.set("service_code", cond.service_code);
    if (cond.dong)        qs.set("dong", cond.dong);
    if (cond.needed_from) qs.set("needed_from", cond.needed_from);
    if (cond.limit)       qs.set("limit", String(cond.limit));
    if (cond.lat != null && cond.lng != null) { qs.set("lat", String(cond.lat)); qs.set("lng", String(cond.lng)); }
    (cond.required_tags  || []).forEach(t => qs.append("required_tags[]", t));
    (cond.preferred_tags || []).forEach(t => qs.append("preferred_tags[]", t));
    return request<any>("GET", `/resource_searches?${qs.toString()}`);
  },
};

// 자원은행 — 기관 제공역량 조회와 가용성 보고.
// 보고(report)는 파라미터가 organization_service 바디 밖 top-level 이라
// 표준 api.create/update 형태와 맞지 않아 별도 헬퍼로 둔다(elderly.updateRisk 와 같은 사유).
export const orgAvailability = {
  list:   (query: Record<string, string> = {}) => {
    const qs = new URLSearchParams(query).toString();
    return request<any>("GET", `/organization_services${qs ? "?" + qs : ""}`);
  },
  report: (id: number | string,
           attrs: { status: string; open_slots?: number; available_from?: string; note?: string }) =>
    request<any>("POST", `/organization_services/${id}/availability`, attrs),
};

// 어르신 위험도 변경 전용. reason이 elderly 바디 밖 top-level 파라미터라
// 표준 api.update({elderly: attrs}) 형태와 맞지 않아 별도 헬퍼로 둔다.
export const elderly = {
  updateRisk: (id: number, risk_level: string, reason: string) =>
    request<any>("PATCH", `/elderlies/${id}`, { elderly: { risk_level }, reason }),
  updateHealth: (id: number, health_status: string, reason: string) =>
    request<any>("PATCH", `/elderlies/${id}`, { elderly: { health_status }, reason }),
};

// 통합돌봄 욕구사정 CRUD. 어르신 하위 index/create + 개별 show/update/approve.
export const needsAssessments = {
  listForElderly: (elderlyId: number) =>
    request<any[]>("GET", `/elderlies/${elderlyId}/needs_assessments`),
  create: (elderlyId: number, attrs: object) =>
    request<any>("POST", `/elderlies/${elderlyId}/needs_assessments`,
                 { needs_assessment: attrs }),
  get:    (id: number) => request<any>("GET",   `/needs_assessments/${id}`),
  update: (id: number, attrs: object) =>
    request<any>("PATCH", `/needs_assessments/${id}`, { needs_assessment: attrs }),
  approve: (id: number) => request<any>("POST",  `/needs_assessments/${id}/approve`),
};

// 통합돌봄 서비스계획 CRUD. 케이스 하위 index/create + 개별 show/update/approve.
// 계획 항목(items) 은 nested 로 함께 전송/수신 (items_attributes).
export const servicePlans = {
  listForCase: (caseId: number) =>
    request<any[]>("GET", `/cases/${caseId}/service_plans`),
  create: (caseId: number, attrs: object) =>
    request<any>("POST", `/cases/${caseId}/service_plans`, { service_plan: attrs }),
  get:    (id: number) => request<any>("GET",   `/service_plans/${id}`),
  update: (id: number, attrs: object) =>
    request<any>("PATCH", `/service_plans/${id}`, { service_plan: attrs }),
  approve: (id: number) => request<any>("POST",  `/service_plans/${id}/approve`),
};

// 케어매니저 소속 변경(운영자 push) 전용 클라이언트. reason이 care_manager 바디 밖
// top-level 파라미터라 표준 api.update 형태와 맞지 않아 별도 헬퍼로 둔다.
export const careManager = {
  transferOrg: (id: number, to_organization_id: number, reason: string) =>
    request<any>("PATCH", `/care_managers/${id}/transfer_org`, { to_organization_id, reason }),
  // 퇴직 처리(명부·매칭에서 제외, 소속·이력 보존) / 복직. reason은 retire에서 필수.
  retire: (id: number, reason: string) =>
    request<any>("PATCH", `/care_managers/${id}/retire`, { reason }),
  // organizationId는 퇴직 중 소속 기관이 삭제돼 미소속이 된 매니저를 복직시킬 때만 필요
  // (재직 매니저는 소속 필수). 소속이 살아 있으면 생략하면 기존 소속 그대로 복직한다.
  unretire: (id: number, reason?: string, organizationId?: number | null) =>
    request<any>("PATCH", `/care_managers/${id}/unretire`, { reason, organization_id: organizationId ?? undefined }),
};

// 응급SOS 전용 클라이언트. 표준 CRUD({resource: attrs}) 형태가 아니라 평탄한
// 바디({sos_type, situation})를 보내므로 별도 헬퍼로 둔다.
export const sos = {
  recent: () => request<any[]>("GET", "/sos"),
  submit: (attrs: { sos_type: string; situation?: string }) =>
    request<any>("POST", "/sos", attrs),
};

// 케어매니저 본인 소속 기관 변경 신청 전용 클라이언트. 표준 CRUD({resource: attrs})
// 형태가 아니라 평탄한 바디(organization_id, reason)를 보내므로 별도 헬퍼로 둔다.
export const orgTransfer = {
  // 신청 화면용: 기관 목록 + 현재 소속 + 진행 중(pending) 신청 상태.
  options: () => request<any>("GET", "/org_transfer_requests/options"),
  request: (organization_id: number, reason: string) =>
    request<any>("POST", "/org_transfer_requests", { organization_id, reason }),
};

// 케어매니저 본인 담당 케이스 이관 신청 전용 클라이언트. 평탄한 바디(case_id,
// to_care_manager_id, reason)를 보내므로 표준 CRUD 헬퍼 대신 별도로 둔다.
export const caseTransfer = {
  // 이관 신청 화면용: 같은 권역 대상 매니저 목록 + 진행 중(pending) 신청 상태.
  candidates: (caseId: number) =>
    request<any>("GET", `/case_transfer_requests/candidates?case_id=${caseId}`),
  request: (case_id: number, to_care_manager_id: number, reason: string) =>
    request<any>("POST", "/case_transfer_requests", { case_id, to_care_manager_id, reason }),
};

// 권역 관리자의 유관기관 연결 계정 해제/이관 전용 클라이언트. 평탄한 바디
// (organization_id, contact_user_id, transfer_action, to_organization_id, reason)를 보낸다.
export const orgContactTransfer = {
  // 화면용: 기관에 연결된 계정 목록(+각 계정 진행 중 신청) + 이관 대상 기관 검색(q).
  options: (organization_id: number, q = "") =>
    request<any>("GET", `/org_contact_transfer_requests/options?organization_id=${organization_id}&q=${encodeURIComponent(q)}`),
  // action은 Rails 라우팅 예약어라 서버는 transfer_action으로 받는다.
  request: (organization_id: number, contact_user_id: number, action: "release" | "transfer", reason: string, to_organization_id?: number) =>
    request<any>("POST", "/org_contact_transfer_requests", { organization_id, contact_user_id, transfer_action: action, to_organization_id, reason }),
};

// 수정내역 보드(Trello형 칸반) 전용 클라이언트. 카드/이동/사진/댓글.
// 사진·댓글이미지는 멀티파트(requestForm), 나머지는 JSON(request).
export const board = {
  load: () => request<any>("GET", "/change_cards"),
  createCard: (attrs: object) => request<any>("POST", "/change_cards", { change_card: attrs }),
  updateCard: (id: number, attrs: object) => request<any>("PATCH", `/change_cards/${id}`, { change_card: attrs }),
  deleteCard: (id: number) => request<void>("DELETE", `/change_cards/${id}`),
  moveCard: (id: number, column_key: string, position: number) =>
    request<any>("PATCH", `/change_cards/${id}/move`, { column_key, position }),
  addPhotos: (id: number, files: File[]) => {
    const fd = new FormData();
    files.forEach(f => fd.append("photos[]", f));
    return requestForm<any>("POST", `/change_cards/${id}/photos`, fd);
  },
  removePhoto: (id: number, photoId: number) => request<any>("DELETE", `/change_cards/${id}/photos/${photoId}`),
  listComments: (id: number) => request<any[]>("GET", `/change_cards/${id}/card_comments`),
  addComment: (id: number, body: string, files: File[]) => {
    const fd = new FormData();
    fd.append("body", body);
    files.forEach(f => fd.append("images[]", f));
    return requestForm<any>("POST", `/change_cards/${id}/card_comments`, fd);
  },
  deleteComment: (id: number, commentId: number) => request<void>("DELETE", `/change_cards/${id}/card_comments/${commentId}`),
};

// 방문 기록 사진 첨부 전용 클라이언트. 사진은 멀티파트(requestForm), 보고서 본문
// 자체는 표준 CRUD(api.create/PATCH)로 다룬다 — 보고서 생성 후 id로 업로드한다.
export const visitReports = {
  addPhotos: (id: number, files: File[]) => {
    const fd = new FormData();
    files.forEach(f => fd.append("photos[]", f));
    return requestForm<any>("POST", `/visit_reports/${id}/photos`, fd);
  },
  removePhoto: (id: number, photoId: number | string) =>
    request<any>("DELETE", `/visit_reports/${id}/photos/${photoId}`),
};

function singular(resource: string): string {
  // Rails-style singular for our resources.
  if (resource === "regions")        return "region";
  if (resource === "organizations")  return "organization";
  if (resource === "care_managers")  return "care_manager";
  if (resource === "inquiries")      return "inquiry";   // -ies → -y (단순 s 제거면 "inquirie")
  if (resource === "elderlies")      return "elderly";   // -ies → -y (단순 s 제거면 "elderlie")
  return resource.replace(/s$/, "");
}
